"use client";

import { useEffect } from "react";
import { getAllProducts } from "@/lib/products";
import { trackCompletePurchase } from "@/lib/analytics/events";
import { hasTrackedOrder, markOrderTracked } from "@/lib/analytics/purchase-dedup";
import { hasAnalyticsConsent } from "@/lib/analytics/types";
import type { LemonSqueezyEvent } from "@/lib/analytics/types";

/**
 * Legacy LemonSqueezy catalog names → product slugs.
 *
 * The LS catalog and the site have drifted through two naming eras
 * ("X Unfollow Pro" vs "Unfollow Pro for X (Twitter)"), and the site renamed
 * its products around 2026-08-06. Exact-string matching against
 * name/alternateName would drop every legacy-named order to "unknown" from
 * that point on. Names below are the ones actually observed in purchase
 * events; extend if LS products are renamed again.
 *
 * NOT the cause of the historical "unknown" purchases, despite appearances.
 * Those are 68 orders named "PlugMonkey Extension" — a single generic LS
 * product used from 2026-05-06 until 2026-07-21, before individual products
 * existed. That name carries no information about WHICH extension was bought,
 * so it is deliberately absent from this map: adding it would invent an
 * attribution rather than recover one, and would silently load every historic
 * bundle sale onto whichever slug was picked. They stay "unknown", which is
 * the only honest answer, and they stopped accruing in July.
 *
 * Checked in PostHog on 2026-08-15 rather than inferred: purchase events
 * grouped by product_name give 68 "PlugMonkey Extension" against 37 across
 * the nine legacy names.
 */
const LEGACY_LS_NAME_TO_SLUG: Record<string, string> = {
  "X Unfollow Pro": "x-unfollow-pro",
  "X Followers Exporter Pro": "x-followers-exporter-pro",
  "Instagram Unfollow Pro": "instagram-unfollow-pro",
  "Instagram Followers Exporter Pro": "instagram-followers-exporter-pro",
  "Video Downloader Pro": "video-downloader-pro",
  "Prompt Anything Pro": "prompt-anything-pro",
  "Reddit Scout Pro": "reddit-scout-pro",
  "ReadMonkey Pro": "readmonkey-pro",
  "LinkedIn Connection Remover Pro": "linkedin-connection-remover-pro",
};

/** Lowercase, strip punctuation/qualifiers so both naming eras compare equal. */
function normalizeLsName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\(.*?\)/g, "") // "(Twitter)" qualifiers
    .replace(/[—–-]/g, " ") // dashes of any era
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Resolve an LS product_name to a catalog product. Order:
 * 1. exact name/alternateName (current era)
 * 2. legacy-name map (old LS catalog era)
 * 3. normalized equality (survives punctuation/qualifier drift)
 * Returns undefined only when nothing matches — caller keeps the "unknown"
 * fallback so an unmatched sale is still counted, never silently attributed.
 */
function resolveLsProduct(productName: string) {
  const products = getAllProducts();

  const exact = products.find(
    (product) => product.name === productName || product.alternateName === productName
  );
  if (exact) return exact;

  const legacySlug = LEGACY_LS_NAME_TO_SLUG[productName];
  if (legacySlug) {
    const legacy = products.find((product) => product.slug === legacySlug);
    if (legacy) return legacy;
  }

  const normalized = normalizeLsName(productName);
  return products.find(
    (product) =>
      normalizeLsName(product.name) === normalized ||
      normalizeLsName(product.alternateName) === normalized
  );
}

/**
 * Lemon Squeezy Event Tracker Component
 *
 * Tracks Checkout.Success events from the Lemon.js overlay — this covers the
 * buyer who pays in the overlay but never clicks through to the thank-you
 * page. The thank-you page dedupes against this via the shared order key, so
 * an order is only ever counted once regardless of which path sees it first.
 */
export default function LemonsqueezyEventTracker() {
  useEffect(() => {
    const eventHandler = (event: LemonSqueezyEvent) => {
      if (event.event !== "Checkout.Success") {
        return;
      }

      try {
        if (!hasAnalyticsConsent()) {
          console.log("Analytics consent not given, skipping conversion tracking");
          return;
        }

        // Coalesce the two documented payload shapes (see LemonSqueezyEvent).
        // Reading only `data.attributes` meant that on the flat shape every
        // field below came back undefined: no identifier, so the dedup guard
        // was skipped and an unkeyed purchase fired that the thank-you page
        // could then fire again; no total, so no revenue; no product, so
        // "unknown".
        const order = event.data;
        const attributes = order?.attributes ?? order;
        const orderIdentifier = attributes?.identifier;

        // The GLOBAL order id — the same value the thank-you URL's [order_id]
        // carries, which is what keeps transaction_id in a single id space
        // whichever path fires. Only the nested shape exposes it. order_number
        // is deliberately not used as a stand-in: it is a per-store sequence
        // from a different id space, so it would silently corrupt the join
        // back to LemonSqueezy rather than fix it.
        const orderId = order?.id != null ? String(order.id) : undefined;

        // Same dedup scope as the thank-you page: whichever path sees the
        // order first claims the purchase fire.
        if (orderIdentifier && hasTrackedOrder("purchase", orderIdentifier)) {
          return;
        }

        // Checkout.Success carries an Order object whose `total` is an
        // integer in CENTS of the order currency (LS Order object docs) —
        // convert to major units before it reaches revenue analytics.
        const rawTotal = attributes?.total;
        const totalNumber = typeof rawTotal === "number" ? rawTotal : Number(rawTotal);
        const total =
          rawTotal != null && Number.isFinite(totalNumber)
            ? (totalNumber / 100).toFixed(2)
            : undefined;

        // Map the LS product name to our product data (exact → legacy map →
        // normalized; see resolveLsProduct). Unmatched products stay
        // "unknown" — the old fallback attributed them to x-unfollow-pro,
        // silently inflating its numbers.
        //
        // The name lives in `first_order_item`, not at the root: no documented
        // Order payload has an `attributes.product_name`, so the previous read
        // resolved nothing and every overlay sale would have recorded as
        // "unknown" regardless of how well resolveLsProduct worked.
        const productName = attributes?.first_order_item?.product_name ?? attributes?.product_name;
        const matchedProduct = productName ? resolveLsProduct(productName) : undefined;

        trackCompletePurchase({
          orderId,
          orderIdentifier: orderIdentifier ?? undefined,
          total,
          // Sent to Google only as a SHA-256 hash (lib/analytics/user-data.ts)
          email: attributes?.user_email,
          product: {
            slug: matchedProduct?.slug || "unknown",
            /*
             * "unknown", never a brand string. This used to fall back to the
             * literal "PlugMonkey Extension" when LS sent no product_name,
             * which fabricates a product that was never named — and because
             * the tracked property is `alternateName || name`, an invented
             * name is indistinguishable in PostHog from a real LS product
             * genuinely called that. 68 purchases between May and July 2026
             * carry that exact value and it is not possible, from the event
             * data alone, to tell which of the two they were.
             *
             * The slug beside it has said "unknown" for exactly this reason
             * since the x-unfollow-pro fallback was removed; the name should
             * have said it too.
             */
            name: matchedProduct?.name || productName || "unknown",
            alternateName: matchedProduct?.alternateName || productName,
            price: matchedProduct?.offers?.lifetime?.price || "0",
          },
        });

        if (orderIdentifier) {
          markOrderTracked("purchase", orderIdentifier);
        }
      } catch (error) {
        console.error("Error tracking Lemon Squeezy conversion:", error);
      }
    };

    const trySetup = (): boolean => {
      if (typeof window === "undefined" || !window.LemonSqueezy) {
        return false;
      }
      window.LemonSqueezy.Setup({ eventHandler });
      return true;
    };

    // lemon.js loads with strategy="afterInteractive", which lands AFTER
    // this effect's first run — the previous one-shot `if (window.LemonSqueezy)`
    // check therefore never subscribed, which is why zero Checkout.Success
    // events exist in PostHog despite months of overlay checkouts. Poll
    // until the SDK shows up; give up after 30s (blocked/failed script).
    if (trySetup()) {
      return;
    }

    const interval = window.setInterval(() => {
      if (trySetup()) {
        window.clearInterval(interval);
      }
    }, 500);
    const timeout = window.setTimeout(() => {
      window.clearInterval(interval);
    }, 30_000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, []);

  // This component doesn't render anything
  return null;
}
